import {
    Expander,
    ExpanderTheme,
    ExpanderButtonStyle,
    ExpanderBorderRoundingStyle,
    ExpanderBorderStyle,
    Panel,
    PanelTheme,
    PanelBorderStyle,
    PanelBorderRadius,
    Button,
    ButtonTheme,
    AddressLocation,
    ProgressSpinner,
} from '@apply/components';
import { PropertyForm } from '../../Property/PropertyForm';
import { ConfirmV2, isMobileScreen } from '@apply/ui';
import { useNavigate } from 'react-router';
import { faTrash, faEdit } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState, useRef } from 'react';
import { useValidateAndSaveApplication } from '../../hooks/useValidateAndSaveApplication';
import { ValidationGroup } from 'devextreme-react';
import { usePausePointTracker } from '../../hooks/usePausePointTracker.tsx';
import { usePropertyStreet1, usePropertyStreet2 } from '../../../queries/usePropertyCoreFields';
import { useDeleteProperty } from '../queries/useDeleteProperty';

export interface PropertyReviewPanelProps {
    propertyGuid: string;
    entryGuid: string | undefined;
    propertyIndex: number;
    printMode?: boolean;
    token: string | null;
    propertyCount: number;
}

export const PropertyReviewPanel: React.FC<PropertyReviewPanelProps> = ({
    propertyGuid,
    entryGuid,
    propertyIndex,
    printMode = false,
    token,
    propertyCount,
}) => {
    const mobileScreen = isMobileScreen();
    const navigate = useNavigate();
    const validationGroupRef = useRef(null);
    const { validateAndSaveApplication } = useValidateAndSaveApplication(validationGroupRef);
    const deleteProperty = useDeleteProperty();

    const [propertyStreet1] = usePropertyStreet1(propertyGuid);
    const [propertyStreet2] = usePropertyStreet2(propertyGuid);
    const [isConfirmDeletePropertyOpen, set_isConfirmDeletePropertyOpen] = useState(false);
    const [isLoading, set_isLoading] = useState(false);
    const { clearPausePoint } = usePausePointTracker();

    const formatLinkDestination = (token?: string | null): string => {
        const qp = `?pid=${propertyGuid ?? ''}${token ? '&token=' + token : ''}`;
        if (entryGuid) {
            return `/property/${entryGuid}${qp}`;
        } else {
            return `/property/${qp}`;
        }
    };

    const editPropertyClick = () => {
        navigate(formatLinkDestination(token));
    };

    const deletePropertyClick = (e: React.MouseEvent<Element, MouseEvent>) => {
        if (e) e.preventDefault();

        if (!propertyGuid) {
            return;
        }

        set_isConfirmDeletePropertyOpen(true);
    };

    const onConfirmDelete = async () => {
        clearPausePoint();

        deleteProperty(propertyGuid);

        set_isLoading(true);
        set_isConfirmDeletePropertyOpen(false);

        const applicationResponse = await validateAndSaveApplication();

        set_isLoading(false);

        if (!applicationResponse) return;

        navigate(`/review/${entryGuid ?? ''}${token ? '?token=' + token : ''}`);
    };

    const onCancelDelete = () => {
        set_isConfirmDeletePropertyOpen(false);
        return;
    };

    const addressLine1 = propertyStreet2 ? `${propertyStreet1} ${propertyStreet2}` : propertyStreet1;
    const renderHeader = () => {
        return (
            <>
                <div className='propertyReviewPanel-header'>
                    <div className='propertyReviewPanel-header-left'>
                        <div className='propertyReviewPanel-header-left-content'>
                            <div className='propertyReviewPanel-header-left-content-label'>
                                <h4>Property</h4>
                            </div>
                            <div className='propertyReviewPanel-header-left-content-address'>
                                <AddressLocation addressLine1={`(${propertyIndex}) ${addressLine1}`} />
                            </div>
                        </div>
                    </div>
                    <div className='propertyReviewPanel-header-right'>
                        <Button
                            id='editPropertyButton'
                            theme={ButtonTheme.LinkBlue}
                            visible={!printMode}
                            handleClick={editPropertyClick}
                            disabled={!entryGuid || !propertyGuid}
                        >
                            {mobileScreen ? <FontAwesomeIcon icon={faEdit} /> : 'Edit'}
                        </Button>
                        {!printMode && propertyCount > 1 && (
                            <Button
                                id='deletePropertyButton'
                                theme={ButtonTheme.LinkRed}
                                handleClick={deletePropertyClick}
                                disabled={!entryGuid || !propertyGuid}
                            >
                                {mobileScreen ? <FontAwesomeIcon icon={faTrash} /> : 'Delete'}
                            </Button>
                        )}
                    </div>
                </div>
            </>
        );
    };

    return (
        <>
            {isLoading && <ProgressSpinner />}
            <ValidationGroup ref={validationGroupRef}>
                <div className='propertyReviewPanel'>
                    <Expander
                        theme={ExpanderTheme.Glass}
                        buttonStyle={ExpanderButtonStyle.Rounded}
                        borderRoundingStyle={ExpanderBorderRoundingStyle.Rounded}
                        borderStyle={ExpanderBorderStyle.Solid}
                        header={renderHeader()}
                        initialExpanded={true}
                        height={isMobileScreen() ? '65px' : '50px'}
                        expandOnButtonClickOnly={true}
                    >
                        <div className={`propertyReviewPanel-content-${isMobileScreen() ? 'mobile' : 'nonMobile'}`}>
                            <Panel
                                theme={PanelTheme.White}
                                borderStyle={isMobileScreen() ? PanelBorderStyle.None : PanelBorderStyle.Solid}
                                borderRadius={PanelBorderRadius.Small}
                            >
                                <PropertyForm
                                    propertyGuid={propertyGuid}
                                    isReview={true}
                                />
                            </Panel>
                        </div>
                    </Expander>
                </div>
            </ValidationGroup>
            <ConfirmV2
                id={`confirmDeletePropertyModal_${propertyGuid}`}
                open={isConfirmDeletePropertyOpen}
                title='Delete Property'
                onConfirm={onConfirmDelete}
                onCancel={onCancelDelete}
                okLabel='Yes'
                cancelLabel='No'
                width='340px'
                height='200px'
            >
                {`Are you sure you want to delete this property (${propertyStreet1} ${propertyStreet2})?`}
            </ConfirmV2>
        </>
    );
};
